/**
 * Citation coverage of a complete Source Intelligence run (Ask BLIP 7544ae15
 * §3 / §3a, 2026-10-21) — internal view only.
 *
 * Shows, per AI model, the share of scored answers that cite at least one
 * source, and the flags the dataset builder will stop on. When the run is
 * flagged, an admin or manager records accept / reject with a reason; the
 * builder then pools the run with every response (accept) or leaves it out
 * and says so (reject). Nothing here changes the run's data.
 */
import { useCallback, useEffect, useState } from 'react';
import type { CoveragePayload } from '@aurora/shared';
import { api, ApiClientError } from '../api/client';
import { useAuth } from '../auth/AuthContext';

const pct = (v: number | null): string =>
  v === null ? '—' : `${(v * 100).toFixed(1)}%`;

export function CoveragePanel({ runId }: { runId: string }): JSX.Element | null {
  const { user } = useAuth();
  const canDecide = user?.role === 'admin' || user?.role === 'manager';
  const [data, setData] = useState<CoveragePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<'accepted' | 'rejected' | ''>('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.getCoverage(runId));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Could not load coverage');
    }
  }, [runId]);
  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <div className="form-error">{error}</div>;
  if (!data) return null;

  const flaggedModels = new Set(
    data.flags.filter((f) => f.model).map((f) => f.model as string),
  );
  const latest = data.reviews[0] ?? null;
  // A clean run can only be "accepted" to withdraw a rejection (server rule).
  const canAccept = data.flags.length > 0 || latest?.decision === 'rejected';

  const submit = async (): Promise<void> => {
    if (!decision) return;
    setBusy(true);
    setFormError(null);
    try {
      setData(await api.reviewCoverage(runId, { decision, reason: reason.trim() }));
      setDecision('');
      setReason('');
    } catch (e) {
      setFormError(e instanceof ApiClientError ? e.message : 'Could not record the review');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card coverage-panel">
      <h3>
        Citation coverage{' '}
        <span className={`qc-badge qc-${data.flags.length ? 'review' : 'pass'}`}>
          {pct(data.run.coverage)}
        </span>{' '}
        <span className="muted">
          {data.datasetId ?? 'no dataset'} · {data.market}
        </span>
      </h3>
      <p className="field-hint">
        Share of scored answers that cite at least one source. Flagged when a
        model cites in under {pct(data.thresholds.model)} of its answers (or
        none), or the run in under {pct(data.thresholds.run)}. A flagged run
        needs an accept or reject before it is pooled into the dataset;
        accepted runs keep every response, including those without citations.
      </p>
      <table className="leads-table">
        <thead>
          <tr>
            <th>Model</th>
            <th>Scored answers</th>
            <th>Cited</th>
            <th>Coverage</th>
          </tr>
        </thead>
        <tbody>
          {data.models.map((m) => (
            <tr key={m.model} className={flaggedModels.has(m.model) ? 'row-flagged' : ''}>
              <td>{m.model}</td>
              <td>{m.responses}</td>
              <td>{m.cited}</td>
              <td>{pct(m.coverage)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.flags.length > 0 && (
        <ul className="approval-blockers">
          {data.flags.map((f, i) => (
            <li key={i}>{f.message}</li>
          ))}
        </ul>
      )}
      {latest ? (
        <p>
          <strong>
            {latest.decision === 'accepted' ? 'Accepted' : 'Rejected'}
          </strong>{' '}
          by {latest.reviewerName ?? latest.reviewerEmail ?? 'a reviewer'} on{' '}
          {new Date(latest.at).toLocaleDateString()}: {latest.reason}
        </p>
      ) : null}
      {data.needsReview && (
        <p className="form-notice">
          {latest
            ? 'New coverage flags since the last decision: this run needs a new review before it can be pooled.'
            : 'Needs a coverage review before this run can be pooled.'}
        </p>
      )}
      {data.purpose === 'source_intelligence' && canDecide && (data.flags.length > 0 || latest) && (
        <div className="reject-form">
          <select
            aria-label="Coverage decision"
            value={decision}
            onChange={(e) => setDecision(e.target.value as 'accepted' | 'rejected' | '')}
          >
            <option value="">{latest ? 'Change the decision…' : 'Decide…'}</option>
            {canAccept && <option value="accepted">Accept into the dataset</option>}
            <option value="rejected">Reject (leave out of the dataset)</option>
          </select>
          <input
            maxLength={2000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (recorded with your name and the date)"
          />
          <button
            type="button"
            className="btn btn-small btn-primary"
            disabled={busy || !decision || reason.trim().length < 10}
            onClick={() => void submit()}
          >
            {busy ? 'Recording…' : 'Record'}
          </button>
        </div>
      )}
      {formError && <div className="form-error">{formError}</div>}
      {data.reviews.length > 1 && (
        <details>
          <summary className="muted">Earlier decisions ({data.reviews.length - 1})</summary>
          <ul className="prompt-history">
            {data.reviews.slice(1).map((r) => (
              <li key={r.id}>
                {r.decision} · {r.reviewerName ?? r.reviewerEmail ?? 'reviewer'} ·{' '}
                {new Date(r.at).toLocaleString()} — {r.reason}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
